import { ReactElement, useEffect, useMemo } from 'react';

// Components
import { Header } from '../../molecules/Header';
import { StandingsFooter } from './components/StandingsFooter';
import { Body } from '../Body';
// Types
import {
  INormalizedKnockout,
  INormalizedKnockoutData,
  INormalizedPlayoffData,
  INormalizedStandingsGroup,
  INormalizedTournamentData,
  IRule,
  IStage,
  IStandingsDataLabels,
  ISubscriberInfo,
  SportType,
} from '../../../../types';
import { Knockout } from '../Knockout';
import { PlayoffKnockout } from '../PlayoffKnockout';
import { ACTIVE } from '../../../../constants/actitivityStatus';
import { useSizeContext } from '../../contexts/SizeContext';
import useResizeObserver from '../../../../hooks/useResizeObserver';
import { MAX_MOBILE_WIDTH } from '../../../../constants/generic';
import { TableVariants } from '../../../../constants/sportType';

export interface IStandingsProps {
  measuredRef?: (node: HTMLDivElement) => void;
  rules?: IRule[];
  seasonId?: string;
  stageList?: IStage[];
  stageId?: IStage['id'];
  secondaryHeader?: ReactElement;
  dataHeaderDisplay?: boolean;
  dataOptionsDisplay?: boolean;
  mobileDropdown?: boolean;
  knockout?: INormalizedKnockoutData; // TODO remove when football knockout data is coming from playoff.api
  standings: INormalizedStandingsGroup[];
  competition?: INormalizedTournamentData;
  dataLabels?: IStandingsDataLabels | null;
  handleActiveStageChange?: (stageId: string) => void;
  handleTeamClick?: (id: string | number, name: string) => void;
  handleEventClick?: (id: string | number) => void;
  handleSeasonChange?: (value?: string) => void;
  handleTournamentClick?: (competitionId?: string, standingsName?: string) => void;
  sport?: SportType;
  playoffStage?: INormalizedPlayoffData | null;
  handleSubscriptionClick: (info: ISubscriberInfo, clickType: string) => void;
  isNational?: boolean;
  isMobile?: boolean;
  displayImage?: boolean;
  tableVariant?: TableVariants;
}

export const Standings = ({
  rules,
  stageId,
  seasonId,
  knockout, // TODO: remove when football knockout data is coming from playoff.api
  standings,
  stageList,
  competition,
  dataLabels,
  secondaryHeader,
  dataHeaderDisplay,
  dataOptionsDisplay,
  mobileDropdown,
  handleTeamClick,
  handleSeasonChange,
  handleTournamentClick,
  handleActiveStageChange,
  sport,
  playoffStage,
  handleEventClick,
  measuredRef: propMeasuredRef,
  handleSubscriptionClick,
  isNational,
  isMobile,
  displayImage,
  tableVariant,
}: IStandingsProps) => {
  useEffect(() => {
    if (!stageId && seasonId && stageList) {
      // If there is no stageId, but there is seasonId, infer stageId from stageList
      // (this happens when season is changed via UI)
      const foundStage = stageList.find((stage) => stage.status === ACTIVE);

      if (foundStage) {
        handleActiveStageChange && handleActiveStageChange(foundStage.id);
      }
    }
  }, [seasonId, stageId, stageList, handleActiveStageChange]);

  const tabOptions = useMemo(
    () =>
      stageList &&
      stageList.map((stage) => ({
        name: stage.name,
        value: stage.id,
      })),
    [stageList],
  );

  const options = useMemo(
    () =>
      competition?.seasons &&
      competition.seasons.map((season) => ({
        label: season.name,
        value: season.id,
      })),
    [competition?.seasons],
  );

  const { setIsMobile } = useSizeContext();
  const standingsMeasuredRef = useResizeObserver((fieldElement) => {
    setIsMobile(fieldElement.offsetWidth < MAX_MOBILE_WIDTH);
  });

  return (
    <>
      <Header
        logo={competition?.logo || competition?.tournamentImage}
        link={competition?.tournamentLink}
        urlTarget={competition?.urlTarget}
        title={competition?.name}
        secondaryTitle={competition?.country?.name}
        selectedOption={seasonId}
        tabOptions={tabOptions}
        options={options}
        selectedTab={stageId}
        secondaryHeader={dataOptionsDisplay ? secondaryHeader : undefined}
        handleSubscriptionClick={handleSubscriptionClick}
        handleChange={handleSeasonChange}
        handleClick={handleTournamentClick}
        handleTabOptionClick={handleActiveStageChange}
        isMainImageRound={playoffStage ? false : isNational}
        displayMainHeader={dataHeaderDisplay == true}
        dataOptionsDisplay={dataOptionsDisplay}
        mobileDropdown={mobileDropdown}
      />

      <Body
        data={standings}
        handleClick={handleTeamClick}
        measuredRef={propMeasuredRef ? propMeasuredRef : standingsMeasuredRef}
        sport={sport}
        isMobile={isMobile}
        displayImage={displayImage}
        tableVariant={tableVariant}
      />

      {knockout && (
        <div className='standings-knockout-container' ref={standingsMeasuredRef}>
          <Knockout
            hasSmallFinal={knockout.hasSmallFinal}
            smallFinalName={knockout.smallFinalName}
            handleEventClick={handleEventClick}
            normalizedKnockoutData={knockout.data as INormalizedKnockout[]}
          />
        </div>
      )}

      {playoffStage && (
        <div className='standings-knockout-container' ref={standingsMeasuredRef}>
          <PlayoffKnockout
            handleEventClick={handleEventClick}
            smallFinal={playoffStage.smallFinal}
            roundNames={playoffStage.roundNames}
            groupsEvents={playoffStage.groupsEvents}
            dataLabels={playoffStage.dataLabels}
            sport={sport}
            type={playoffStage.type}
            isNational={isNational}
          />
        </div>
      )}

      {knockout || playoffStage ? null : <StandingsFooter rules={rules} sport={sport} dataLabels={dataLabels} />}
    </>
  );
};
